/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, Fragment } from 'react';
import PropTypes, { oneOf } from 'prop-types';
import { createUseStyles } from 'react-jss';
import { Input } from '@ui5/webcomponents-react';
import { SuggestionItem } from '@ui5/webcomponents-react';
import { InputType } from '@ui5/webcomponents-react';
import { Icon } from '@ui5/webcomponents-react';
import { suggestionsApiService } from './suggestionsApiService';
import { useMounted } from '../../hooks';

const inputStyle = {
  width: '100%',
};
const searchIconStyle = {
  color: '#0854A1',
};
const iconStyle = {
  color: '#0854A1',
  marginRight: '6px',
  // marginTop: '3px',
};
const styles = () => ({
  '@keyframes loadingAnimation': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },
  loadingSearch: {
    animation: '$loadingAnimation 1.2s linear infinite',
  },
});

const getSuggestItemText = (mode, suggestionItemText, item) => {
  return mode === 'dropdown' && suggestionItemText !== ''
    ? `${item[suggestionItemText]}`
    : `${item}`;
};

const getSuggestItemInfo = (mode, suggestionItemValue, item) => {
  return mode === 'dropdown' && suggestionItemValue !== '' ? `${item[suggestionItemValue]}` : '';
};

const getSuggestItemDescription = (mode, suggestionItemDesc, item) => {
  return mode === 'dropdown' && suggestionItemDesc !== '' ? `${item[suggestionItemDesc]}` : '';
};

const doSuggestionDone = ({ suggestions, isMounted, limitSuggestionSize, setSuggestions }) => {
  if (!isMounted.current) {
    return;
  }
  // limit Suggestion Size
  if (limitSuggestionSize > 0 && suggestions.length > limitSuggestionSize) {
    suggestions = suggestions.slice(0, limitSuggestionSize);
  }
  setSuggestions(suggestions);
};

const doSuggestionError = ({ isMounted, err }) => {
  if (isMounted.current) {
    console.log('Error: ', err);
  }
};

const doSuggestionFinally = ({ isMounted, setIsLoading }) => {
  if (isMounted.current) {
    setIsLoading(false);
  }
};

const fetchSuggestions = ({
  term,
  searchValue,
  setSearchValue,
  showSuggestions,
  startSuggestLength,
  setIsLoading,
  suggestionApi,
  isMounted,
  limitSuggestionSize,
  setSuggestions,
}) => {
  if (term && term !== searchValue.text) {
    setSearchValue({ key: '', text: term });
    if (!showSuggestions) {
      return;
    }
    if (term.length < startSuggestLength) {
      return;
    }
    setIsLoading(true);
    suggestionsApiService
      .getSuggestions(suggestionApi, term)
      .then(suggestions =>
        doSuggestionDone({ suggestions, isMounted, limitSuggestionSize, setSuggestions }),
      )
      .catch(err => doSuggestionError({ isMounted, err }))
      .finally(() => doSuggestionFinally({ isMounted, setIsLoading }));
  }
};

const doInput = ({
  evt,
  onInput,
  searchValue,
  setSearchValue,
  setSuggestions,
  mode,
  onSuggestionSelect,
  value,
  showSuggestions,
  startSuggestLength,
  setIsLoading,
  suggestionApi,
  isMounted,
  limitSuggestionSize,
}) => {
  evt.preventDefault();
  if (onInput) {
    onInput(evt);
  }

  const term = evt.target.value;
  setSearchValue({ key: '', text: term });
  setSuggestions([]);

  if (mode === 'searchbox') {
    onSuggestionSelect({ key: '', text: term });
  }

  if (value !== term) {
    fetchSuggestions({
      term,
      searchValue,
      setSearchValue,
      showSuggestions,
      startSuggestLength,
      setIsLoading,
      suggestionApi,
      isMounted,
      limitSuggestionSize,
      setSuggestions,
    });
  }
};

const doBlur = (e, value, setSearchValue) => {
  if (value !== undefined) {
    setSearchValue(prevSearchValue => ({ ...prevSearchValue, text: value || '' }));
  }
};

const getEmptyIcon = (mode, onSearchButtonClick) => {
  return mode === 'searchbox' ? (
    <Icon id="icon-search" name={'search'} onClick={onSearchButtonClick} style={searchIconStyle} />
  ) : (
    <span></span>
  );
};

const getIcon = ({
  searchValue,
  mode,
  onSearchButtonClick,
  isLoading,
  classes,
  allowClear,
  setSearchValue,
  setSuggestions,
  onClear,
}) => {
  return searchValue.text.length === 0 ? (
    <div style={{ display: 'flex', marginRight: '0' }}>
      {getEmptyIcon(mode, onSearchButtonClick)}
    </div>
  ) : (
    <Fragment>
      {mode === 'searchbox' ? (
        <div style={{ display: 'flex', marginRight: '5px' }}>
          {isLoading && (
            <Icon
              id="icon-loading-search"
              data-testid="icon-loading-search"
              name={'synchronize'}
              className={classes.loadingSearch}
              style={iconStyle}
            />
          )}

          {allowClear && (
            <Icon
              id="icon-cancel"
              data-testid="icon-cancel"
              name={'sys-cancel'}
              onClick={() => {
                setSearchValue({ key: '', text: '' });
                setSuggestions([]);
                onClear && onClear();
              }}
              style={iconStyle}
            />
          )}

          <Icon
            id="icon-search"
            data-testid="icon-search"
            name={'search'}
            onClick={onSearchButtonClick}
            style={iconStyle}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', marginRight: isLoading ? '5px' : '-5px' }}>
          {isLoading && (
            <Icon
              id="icon-loading-search"
              data-testid="icon-loading-search"
              name={'synchronize'}
              className={classes.loadingSearch}
              style={iconStyle}
            />
          )}

          {allowClear && (
            <Icon
              id="icon-cancel"
              data-testid="icon-cancel"
              name={'sys-cancel'}
              onClick={() => {
                setSearchValue({ key: '', text: '' });
                setSuggestions([]);
                onClear && onClear();
              }}
              style={iconStyle}
            />
          )}
        </div>
      )}
    </Fragment>
  );
};

const SearchInput = ({
  mode = 'dropdown',
  value,
  style,
  startSuggestLength = 3,
  limitSuggestionSize = 20,
  placeholder = 'Input search text',
  showSuggestions = true,
  allowClear = true,
  suggestionItemText = '',
  suggestionItemValue = '',
  suggestionItemDesc = '',
  suggestionApi,
  onSubmit,
  onInput,
  onSearchButtonClick,
  onSuggestionSelect,
  onClear,
  ...restProps
}) => {
  const useStyles = createUseStyles(styles);
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState({
    key: '',
    text: '',
  });
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const isMounted = useMounted();

  useEffect(() => {
    fetchSuggestions({
      term: value,
      searchValue,
      setSearchValue,
      showSuggestions,
      startSuggestLength,
      setIsLoading,
      suggestionApi,
      isMounted,
      limitSuggestionSize,
      setSuggestions,
    });
  }, [value]);

  return (
    <Input
      {...restProps}
      style={{ ...inputStyle, ...style }}
      placeholder={placeholder}
      type={InputType.Text}
      value={searchValue.text}
      data-testid="search-input"
      onInput={evt =>
        doInput({
          evt,
          onInput,
          searchValue,
          setSearchValue,
          setSuggestions,
          mode,
          onSuggestionSelect,
          value,
          showSuggestions,
          startSuggestLength,
          setIsLoading,
          suggestionApi,
          isMounted,
          limitSuggestionSize,
        })
      }
      onBlur={e => doBlur(e, value, setSearchValue)}
      onSubmit={onSubmit}
      icon={getIcon({
        searchValue,
        mode,
        onSearchButtonClick,
        isLoading,
        classes,
        allowClear,
        setSearchValue,
        setSuggestions,
        onClear,
      })}
      showSuggestions={showSuggestions}
      onSuggestionItemSelect={evt => {
        console.log(evt.detail.item.attributes);
        const key = evt.detail.item.attributes.info.value;
        const text = evt.detail.item.attributes.text.value;
        if (mode === 'dropdown') {
          setSearchValue({ key, text });
          onSuggestionSelect({ key, text });
        } else {
          setSearchValue({ key: '', text });
          onSuggestionSelect({ key, text });
        }
      }}
    >
      {Array.isArray(suggestions) &&
        suggestions.map((item, idx) => (
          <SuggestionItem
            data-testid="suggestion-item"
            key={`suggestion-${idx}`}
            text={getSuggestItemText(mode, suggestionItemText, item)}
            info={getSuggestItemInfo(mode, suggestionItemValue, item)}
            description={getSuggestItemDescription(mode, suggestionItemDesc, item)}
          />
        ))}
    </Input>
  );
};

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  mode: oneOf(['dropdown', 'searchbox']),
  startSuggestLength: PropTypes.number,
  limitSuggestionSize: PropTypes.number,
  showSuggestions: PropTypes.bool,
  allowClear: PropTypes.bool,
  suggestionItemText: PropTypes.string,
  suggestionItemValue: PropTypes.string,
  suggestionItemDesc: PropTypes.string,
  suggestionApi: PropTypes.string,
  onSubmit: PropTypes.func,
  onInput: PropTypes.func,
  onSearchButtonClick: PropTypes.func,
  onSuggestionSelect: PropTypes.func,
};

SearchInput.propDescriptions = {
  mode: 'Search input mode',
  startSuggestLength:
    'User type characpter more than this length, start to connect backend suggestion api',
  limitSuggestionSize: 'Show the limit size of suggestion items',
  allowClear: 'Show icon button to clear the input value',
  suggestionItemText: 'Defines the text of the ui5-suggestion-item',
  suggestionItemValue: 'Defines the info, displayed in the end of the item',
  suggestionItemDesc:
    'Defines the description displayed right under the item text, if such is present',
  suggestionApi: 'Auto complete suggestion API',
  onSubmit: 'Fired when user presses Enter key on the ui5-input',
  onInput:
    'Fired when the value of the ui5-input changes at each keystroke, and when a suggestion item has been selected',
  onSearchButtonClick: 'Fired when user click search button',
  onSuggestionSelect:
    'Fired when a suggestion item, that is displayed in the suggestion popup, is selected',
};

export default SearchInput;
